import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs';
import { fade_in, fade_out_absolute } from 'src/app/core/animations/fade';
import { RouterService } from 'src/app/core/services/router/router.service';
import { ConfirmDeleteModalComponent } from '../confirm-delete-modal/confirm-delete-modal.component';
import { ClinicDetailQuery } from '../../state/clinic-detail/clinic-detail.query';
import { ClinicDetailState } from '../../state/clinic-detail/clinic-detail.store';
import { HeaderPictureQuery } from '../../state/header-picture/header-picture.query';
import { SettingsQuery } from '../../state/settings/settings.query';
import { UserQuery } from '../../state/user/user.query';
import { UserService } from '../../state/user/user.service';
import { UserState } from '../../state/user/user.store';
import { InputSearchComponent } from '../input-search/input-search.component';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';

@Component({
    standalone: false,
    selector: 'app-header-desktop',
    animations: [fade_in, fade_out_absolute],
    templateUrl: './header-desktop.component.html',
    styleUrls: ['./header-desktop.component.scss'],
})
export class HeaderDesktopComponent implements OnInit {
    @Input() navigationBack = true; //TODO: QUien implemente esto, tiene que tener en cuenta que si hay solo 1 clínica no debe de mostrar el botón
    @Input() backIcon = 'close-outline';
    @Input() loginButton = true;
    @Input() logoUrl = '';
    @Input() search = false;
    @Input() login = false;
    @Input() showSkeleton = false;
    @Input() skeletonBackButton = false;
    @Input() hideSearch = true;
    @Input() clinicName = '';
    @Input() title = '';
    @Input() favorite = false;
    @Input() searchLoading = false;
    @Input() topBarVariant?: 'home';
    @Input() collapsed = false;
    @Output() onClickBack = new EventEmitter<void>();
    @Output() onFavoriteChange = new EventEmitter<void>();
    @Output() onWriteSearch = new EventEmitter<string>();
    @Output() onSearch = new EventEmitter<string>();
    @Output() onShowSearch = new EventEmitter<void>();
    @Output() onHideSearch = new EventEmitter<void>();
    @Output() onClickInputSearch = new EventEmitter<void>();

    @ViewChild(InputSearchComponent) inputSearch: InputSearchComponent;
    @ViewChild('homeTopBar') homeTopBarRef: ElementRef<HTMLElement>;

    private readonly topBarSearchTextChanged: Subject<string> = new Subject<string>();
    searchValue = '';
    isSearching = false;
    searchActive = false;

    user: UserState;
    user$: Observable<UserState>;
    isGeneric$: Observable<boolean>;
    clinic$: Observable<ClinicDetailState>;
    
    private readonly routesLargeLogo: string[] = ['details'];

    public showLargeLogo: boolean = true;
    public isNative: boolean = Capacitor.isNativePlatform();
    public isFlowwwer: boolean = !!environment.genericKey;
    public isCustom: boolean = !!environment.systemKey;
    public isBrowser: boolean = !!environment.browser;

    private router$?: Subscription;

    constructor(
        private router: Router,
        private settingsQuery: SettingsQuery,
        private clinicDetailQuery: ClinicDetailQuery,
        private userQuery: UserQuery,
        private modalController: ModalController,
        private userService: UserService,
        private routerService: RouterService,
        private headerPicturesQuery: HeaderPictureQuery
    ) {
        this.user$ = this.userQuery.select();
        this.user$.subscribe((user) => {
            this.user = user;
        });

        this.clinic$ = this.clinicDetailQuery.select();
    }

    ngOnInit(): void {
        this.isGeneric$ = this.settingsQuery.selectIsGeneric();
        if (this.logoUrl === '') {
            this.headerPicturesQuery
                .select('homeCompany')
                .subscribe((homeCompany) => {
                    this.logoUrl = homeCompany;
                });
        }

        this.router$ = this.router.events
        .pipe(
            filter((event) => event instanceof NavigationEnd),
            map( (e: NavigationEnd) => e.url),
            distinctUntilChanged()
        )
        .subscribe( (path: string) => {
            const url = path.split('/').pop();
            this.showLargeLogo = this.routesLargeLogo.includes(url);
        });

        this.topBarSearchTextChanged
            .pipe(
                tap(() => (this.isSearching = true)),
                debounceTime(250),
                tap(() => (this.isSearching = false)),
                distinctUntilChanged()
            )
            .subscribe((text) => {
                if (text !== this.searchValue) {
                    return;
                }
                this.onWriteSearch.emit(text);
            });
    }

    clickBackButton(): void {
        this.onClickBack.emit();
    }

    clickFavorite(): void {
        this.onFavoriteChange.emit();
    }

    get userImage() {
        const backgroundColor = document.documentElement.style
            .getPropertyValue('--ion-color-secondary')
            .substring(1);
        const textColor = document.documentElement.style
            .getPropertyValue('--ion-color-primary')
            .substring(1);

        return `https://eu.ui-avatars.com/api/?name=${encodeURIComponent(
            this.user.nameFull
        )}&background=${backgroundColor}&color=${textColor}&rounded=false&size=120`;
    }

    async showConfirmLogout() {
        const modal = await this.modalController.create({
            component: ConfirmDeleteModalComponent,
            cssClass: 'modal-confirm-delete',
            componentProps: {
                icon: 'log_out',
                titleKey: 'app_con_logout',
                subtitleKey: 'app_con_logout_sub',
                confirmLabelKey: 'app_ask_sched_step_3_button',
                cancelLabelKey: 'app_action_cancel',
            },
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.confirmed) {
            this.logout();
        }
    }

    logout() {
        this.userService.deleteCurrentTokenLocalStorage();
        this.userService.resetStore();
        this.routerService.goToMainTabWithoutLogin();
    }

    signUp() {
        this.router.navigate([
            '/' +
                this.settingsQuery.systemKeyURL +
                '/' +
                this.clinicDetailQuery.id +
                '/register/social'
        ]);
    }

    writeInputSearch(searchText) {
        if (searchText) {
            this.onShowSearch.emit();
        } else {
            this.onHideSearch.emit();
        }
        this.onWriteSearch.emit(searchText);
    }

    searchInputSearch(searchText) {
        this.onSearch.emit(searchText);
    }

    clearSearchInput() {
        this.searchValue = '';
        this.searchActive = false;
        this.inputSearch?.clearInput();
    }

    clickShowHideButton() {
        this.onHideSearch.emit();
    }

    clickInputSearch(): void {
        this.onClickInputSearch.emit();
    }

    topBarSearchFocus(): void {
        if (!this.searchActive) {
            this.searchActive = true;
            this.onShowSearch.emit();
        }
    }

    topBarSearchChange(searchText: string): void {
        this.searchValue = searchText;
        if (searchText) {
            const wasActive = this.searchActive;
            this.searchActive = true;
            if (!wasActive) {
                this.refocusTopBarSearch();
            }
            this.onShowSearch.emit();
        }
        this.topBarSearchTextChanged.next(searchText);
    }

    topBarSearchSubmit(): void {
        this.onSearch.emit(this.searchValue);
    }

    topBarSearchClear(): void {
        this.topBarSearchCancel();
    }

    topBarSearchCancel(): void {
        this.searchValue = '';
        this.isSearching = false;
        this.searchActive = false;
        this.onHideSearch.emit();
        this.onClickBack.emit();
    }

    topBarHomeBack(): void {
        this.searchValue = '';
        this.isSearching = false;
        this.searchActive = false;
        this.onHideSearch.emit();
        this.onClickBack.emit();
    }

    private refocusTopBarSearch(attemptsLeft = 20): void {
        requestAnimationFrame(() => {
            const searchEl = this.homeTopBarRef?.nativeElement?.shadowRoot?.querySelector(
                'flw-search'
            ) as (HTMLElement & { setFocus?: () => Promise<void> }) | null;

            if (searchEl?.setFocus) {
                searchEl.setFocus();
                return;
            }
            if (attemptsLeft > 0) {
                this.refocusTopBarSearch(attemptsLeft - 1);
            }
        });
    }

    ngOnDestroy() {
        this.router$?.unsubscribe();
        this.topBarSearchTextChanged.complete();
    }

}
